import { useQuery, UseQueryResult } from '@tanstack/react-query';
import manufacturingDashboardService from '../services/manufacturing-dashboard.service';

// Type definitions for dashboard data
export interface ProductionOverview {
  totalWorkOrders: number;
  activeWorkOrders: number;
  completedToday: number;
  pendingInspection: number;
  statusDistribution: { status: string; count: number }[];
  productionRate: number;
  utilizationRate: number;
}

export interface KPIMetrics {
  oee: number;
  oeeBasis?: string;
  firstPassYield: number;
  rejectionRate: number;
  scrapRate: number;
  onTimeDelivery: number;
  otif?: number;
  otifDueLines?: number;
  otifHitLines?: number;
  avgCycleTime: number;
  capacityUtilization: number;
  operatorEfficiency: number;
}

export interface ProductionTrend {
  date: string;
  planned: number;
  produced: number;
  rejected: number;
  efficiency: number;
}

export interface WorkstationStatus {
  workstationId: string;
  workstationName: string;
  status: 'Available' | 'In Use' | 'Maintenance' | 'Breakdown' | 'Idle';
  currentJob?: {
    workOrderNumber: string;
    operationName: string;
    progress: number;
  };
  assignedOperator?: {
    id: string;
    name: string;
  };
  utilizationToday: number;
  efficiency: number;
  nextScheduledJob?: {
    workOrderNumber: string;
    scheduledTime: string;
  };
}

export interface ScheduleOverview {
  workOrderNumber: string;
  workOrderId: string;
  itemName: string;
  stages: {
    stageName: string;
    workstation: string;
    plannedStart: string;
    plannedEnd: string;
    actualStart?: string;
    actualEnd?: string;
    status: string;
    progress: number;
  }[];
}

export interface QualityMetrics {
  totalInspections: number;
  pendingInspections: number;
  passedInspections: number;
  failedInspections: number;
  passRate: number;
  defectRate: number;
  topDefectReasons: { reason: string; count: number }[];
  inspectionTrend: { date: string; passed: number; failed: number }[];
}

export interface ResourceTracking {
  activeOperators: number;
  totalOperators: number;
  operatorList: {
    operatorId: string;
    operatorName: string;
    currentOperation?: string;
    workstation?: string;
    efficiency: number;
    hoursWorked: number;
  }[];
  materialConsumption: {
    itemName: string;
    consumed: number;
    planned: number;
    variance: number;
  }[];
}

export interface Alert {
  id: string;
  type: 'bottleneck' | 'delay' | 'quality' | 'resource' | 'maintenance';
  severity: 'critical' | 'warning' | 'info';
  title: string;
  description: string;
  workOrderId?: string;
  workstationId?: string;
  timestamp: string;
  actionRequired?: string;
}

export interface DashboardAlerts {
  critical: Alert[];
  warnings: Alert[];
  info: Alert[];
}

/**
 * Hook to fetch production overview
 * @param date - Optional date string (YYYY-MM-DD)
 * @param refreshInterval - Auto-refresh interval in milliseconds (default: 30000 - 30 seconds)
 */
export const useProductionOverview = (
  date?: string,
  refreshInterval?: number
): UseQueryResult<ProductionOverview, Error> => {
  return useQuery({
    queryKey: ['manufacturing-dashboard', 'overview', date],
    queryFn: async () => {
      const response = await manufacturingDashboardService.getOverview(date);
      return response.data?.data || response.data;
    },
    refetchInterval: refreshInterval || 30000, // 30 seconds default
    staleTime: 10000, // Consider data fresh for 10 seconds
  });
};

/**
 * Hook to fetch KPI metrics
 * @param period - Time period: today, week, or month
 * @param refreshInterval - Auto-refresh interval in milliseconds (default: 60000 - 1 minute)
 */
export const useKPIMetrics = (
  period: 'today' | 'week' | 'month' = 'today',
  refreshInterval?: number
): UseQueryResult<KPIMetrics, Error> => {
  return useQuery({
    queryKey: ['manufacturing-dashboard', 'kpis', period],
    queryFn: async () => {
      const response = await manufacturingDashboardService.getKPIs(period);
      return response.data?.data || response.data;
    },
    refetchInterval: refreshInterval || 60000, // 1 minute default
    staleTime: 30000,
  });
};

/**
 * Hook to fetch production trends
 * @param days - Number of days to retrieve (1-90)
 */
export const useProductionTrends = (
  days: number = 7
): UseQueryResult<ProductionTrend[], Error> => {
  return useQuery({
    queryKey: ['manufacturing-dashboard', 'trends', days],
    queryFn: async () => {
      const response = await manufacturingDashboardService.getTrends(days);
      return response.data?.data || response.data;
    },
    staleTime: 5 * 60 * 1000, // 5 minutes - trends don't change frequently
  });
};

/**
 * Hook to fetch workstation status
 * @param refreshInterval - Auto-refresh interval in milliseconds (default: 30000 - 30 seconds)
 */
export const useWorkstationStatus = (
  refreshInterval?: number
): UseQueryResult<WorkstationStatus[], Error> => {
  return useQuery({
    queryKey: ['manufacturing-dashboard', 'workstations'],
    queryFn: async () => {
      const response = await manufacturingDashboardService.getWorkstations();
      return response.data?.data || response.data;
    },
    refetchInterval: refreshInterval || 30000, // 30 seconds default
    staleTime: 10000,
  });
};

/**
 * Hook to fetch schedule overview for Gantt chart
 * @param startDate - Start date (YYYY-MM-DD)
 * @param endDate - End date (YYYY-MM-DD)
 */
export const useScheduleOverview = (
  startDate: string,
  endDate: string
): UseQueryResult<ScheduleOverview[], Error> => {
  return useQuery({
    queryKey: ['manufacturing-dashboard', 'schedule', startDate, endDate],
    queryFn: async () => {
      const response = await manufacturingDashboardService.getSchedule(startDate, endDate);
      return response.data?.data || response.data;
    },
    enabled: !!startDate && !!endDate,
    staleTime: 2 * 60 * 1000, // 2 minutes
  });
};

/**
 * Hook to fetch quality metrics
 * @param period - Time period: today, week, or month
 */
export const useQualityMetrics = (
  period: 'today' | 'week' | 'month' = 'today'
): UseQueryResult<QualityMetrics, Error> => {
  return useQuery({
    queryKey: ['manufacturing-dashboard', 'quality', period],
    queryFn: async () => {
      const response = await manufacturingDashboardService.getQuality(period);
      return response.data?.data || response.data;
    },
    staleTime: 2 * 60 * 1000, // 2 minutes
  });
};

/**
 * Hook to fetch resource tracking (operators and materials)
 * @param refreshInterval - Auto-refresh interval in milliseconds (default: 60000 - 1 minute)
 */
export const useResourceTracking = (
  refreshInterval?: number
): UseQueryResult<ResourceTracking, Error> => {
  return useQuery({
    queryKey: ['manufacturing-dashboard', 'resources'],
    queryFn: async () => {
      const response = await manufacturingDashboardService.getResources();
      return response.data?.data || response.data;
    },
    refetchInterval: refreshInterval || 60000, // 1 minute default
    staleTime: 30000,
  });
};

/**
 * Hook to fetch dashboard alerts
 * @param refreshInterval - Auto-refresh interval in milliseconds (default: 30000 - 30 seconds)
 */
export const useDashboardAlerts = (
  refreshInterval?: number
): UseQueryResult<DashboardAlerts, Error> => {
  return useQuery({
    queryKey: ['manufacturing-dashboard', 'alerts'],
    queryFn: async () => {
      const response = await manufacturingDashboardService.getAlerts();
      return response.data?.data || response.data;
    },
    refetchInterval: refreshInterval || 30000, // 30 seconds default for real-time alerts
    staleTime: 10000,
  });
};
